import {Table} from "@frostbyte-technologies/frostbyte-tailwind";
import Fuse from "fuse.js";
import React, {Component} from "react";
import {classNames, minutesToHours, toDollars} from "@frostbyte-technologies/frostbyte-core/dist/utils/util";
import AdditionalTipsModal from "../../../modals/payroll/earnings/additional-tips-modal";
import SupplementalModal, {SUPPLEMENTAL_TYPES} from "../../../modals/payroll/earnings/supplemental-modal";
import DangerBadge from "../../../components/badges/danger-badge";
import ExtraPayModal, {PAY_TYPES} from "../../../modals/payroll/earnings/extra-pay-modal";
import ReimbursementModal from "../../../modals/payroll/earnings/reimbursement-modal";
import {setupReduxConnection} from "../../../redux";
import {fetchPayrollTabs, hasNoPayTimeCard} from "../../../utils/payroll-helper";
import {doLocationsHaveDoubleOvertime} from "../../reporting/reports/reporting-constants";
import TimeCardSummaryExpansionComponent from "../../reporting/reports/time-card-summary-expansion-component";
import PayrollRunTableFilter from "./payroll-run-table-filter";

class PayrollRunTable extends Component {
  state = {search: "", selectedLocation: "all", selectedEmployees: "with-pay"};

  getBreakdownColumns() {
    let {
      checkPayrollId,
      extras,
      hideEarned,
      prefixColumns = [],
      hideExtraAdditions,
      dbaLocations,
    } = this.props;
    const {location, payrollGroup} = this.props.shop;

    let {PAYROLL_PAY_CALCULATED_TIPS, RUN_PAYROLL_SHOW_COMMISSION, AUTOMATIC_TIP_CREDIT} =
      this.props.shop.settings;

    let grossPayTitle = hideEarned ? "Reimbursements" : "Gross Pay";

    const showDoubleOvertime = doLocationsHaveDoubleOvertime(location, payrollGroup);

    let columns = [
      {
        label: "Employee Name",
        value: "NAME",
        tooltip: "Names marked in red have a time card with no pay earned",
        format: (value, row) => {
          if (hasNoPayTimeCard(row.TIMESHEETS)) {
            return <DangerBadge>{row.FULL_NAME}</DangerBadge>;
          }

          return <div className="text-gray-800">{row.FULL_NAME ?? "Total"}</div>;
        },
        sortable: true,
        sortFunction: (a, b) => {
          if (!a?.FULL_NAME || b.FULL_NAME) {
            return 0;
          }
          return a?.FULL_NAME > b?.FULL_NAME ? -1 : 1;
        },
      },
    ];

    let timeWorked = {
      label: "Regular",
      value: "AMOUNT_MINUTES",
      format: (value, row) => {
        if (row.TYPE === "SAL") {
          return "Salary";
        }

        if (row.PAY_TYPE === "OTHER") {
          let regularHoursTotal = extras[row.ID]?.REGULAR_HOURS_TOTAL;
          let regularAmountTotal = extras[row.ID]?.REGULAR_AMOUNT_TOTAL;

          return this.renderExtraPay(
            row,
            checkPayrollId,
            PAY_TYPES.REGULAR.VALUE,
            regularHoursTotal,
            regularAmountTotal
          );
        }

        return (
          <div className="text-left text-gray-800">
            <div className="text-medium">{minutesToHours(row?.PAY_SUMMARY?.AMOUNT_REGULAR_MINUTES)} hrs</div>

            <div className="text-gray-500">{toDollars(row?.PAY_SUMMARY?.AMOUNT_REGULAR_GROSS, true)}</div>
          </div>
        );
      },
    };

    let overtimeWorkedArr = [
      {
        label: "Overtime",
        value: "AMOUNT_MINUTES",
        format: (value, row) => {
          if (row.TYPE === "SAL") {
            return "";
          }

          if (row.PAY_TYPE === "OTHER") {
            let overtimeHoursTotal = extras[row.ID]?.OVERTIME_HOURS_TOTAL;
            let overtimeAmountTotal = extras[row.ID]?.OVERTIME_AMOUNT_TOTAL;

            return this.renderExtraPay(
              row,
              checkPayrollId,
              PAY_TYPES.OVERTIME.VALUE,
              overtimeHoursTotal,
              overtimeAmountTotal
            );
          }

          let minutes = row.PAY_SUMMARY?.AMOUNT_OVERTIME_MINUTES ?? 0;

          return (
            <div className={classNames("text-left", !minutes && "text-gray-300")}>
              <div className={classNames("text-medium", minutes && "text-gray-800")}>
                {minutesToHours(minutes)} hrs
              </div>

              <div className={classNames(minutes && "text-gray-500")}>
                {toDollars(row?.PAY_SUMMARY?.AMOUNT_OVERTIME_GROSS, true)}
              </div>
            </div>
          );
        },
      },
    ];

    if (showDoubleOvertime) {
      overtimeWorkedArr.push({
        label: "Double Overtime",
        value: "AMOUNT_MINUTES",
        format: (value, row) => {
          if (row.TYPE === "SAL") {
            return "";
          }

          let minutes = row.PAY_SUMMARY?.AMOUNT_DOUBLE_OVERTIME_MINUTES ?? 0;

          if (row.PAY_TYPE === "OTHER") {
            let doubleOvertimeHoursTotal = extras[row.ID]?.DOUBLE_OVERTIME_HOURS_TOTAL;
            let doubleOvertimeAmountTotal = extras[row.ID]?.DOUBLE_OVERTIME_AMOUNT_TOTAL;

            return this.renderExtraPay(
              row,
              checkPayrollId,
              PAY_TYPES.DOUBLE_OVERTIME.VALUE,
              doubleOvertimeHoursTotal,
              doubleOvertimeAmountTotal
            );
          }

          return (
            <div className={classNames("text-left", !minutes && "text-gray-300")}>
              <div className={classNames("text-medium", minutes && "text-gray-800")}>
                {minutesToHours(minutes)} hrs
              </div>

              <div className={classNames(minutes && "text-gray-500")}>
                {toDollars(row?.PAY_SUMMARY?.AMOUNT_DOUBLE_OVERTIME_GROSS, true)}
              </div>
            </div>
          );
        },
      });
    }

    let payEarned = {
      label: "Total",
      value: "AMOUNT_PAYED",
      format: (value, row) => {
        if (row.TYPE === "SAL") {
          return `${toDollars(row.RATE, true)}/yr`;
        }

        let regularHoursTotal = extras[row.ID]?.REGULAR_HOURS_TOTAL ?? 0;
        let regularAmountTotal = extras[row.ID]?.REGULAR_AMOUNT_TOTAL ?? 0;

        return (
          <div className="text-left text-gray-800 font-medium">
            <div className="text-medium">
              {minutesToHours(row?.PAY_SUMMARY?.AMOUNT_MINUTES ?? 0 + regularHoursTotal * 60)} hrs
            </div>

            <div className="text-gray-500">
              {toDollars(row?.PAY_SUMMARY?.AMOUNT_STANDARD_PAY ?? 0 + regularAmountTotal ?? 0, true)}
            </div>
          </div>
        );
      },
    };

    let tips = {
      width: 1,
      label: "Tips",
      value: "AMOUNT_TIPS",
      format: (value, row) => {
        if (!row.ACCOUNT_ID) {
          return <div className="text-gray-800">{toDollars(extras["TOTALS"].AMOUNT_TIPS, true)}</div>;
        }

        return (
          <div>
            {PAYROLL_PAY_CALCULATED_TIPS === "1" && !hideEarned && (
              <div className="text-gray-800" style={{marginBottom: 3}}>
                {toDollars(row?.PAY_SUMMARY?.AMOUNT_TIPS ?? 0, true)}
              </div>
            )}

            {extras[row.ID]?.ADDITIONAL_TIPS?.map((additionalTip) => {
              let type = additionalTip.TYPE === "paycheck_tips" ? "PT" : "CT";
              return (
                <div
                  className="text-indigo-500 font-semibold cursor-pointer"
                  onClick={() =>
                    this.additionalTipsModal.open(
                      checkPayrollId,
                      row.CHECK_EMPLOYEE_ID,
                      row.LOCATION_ID,
                      row.FULL_NAME,
                      additionalTip
                    )
                  }
                >
                  {`${type}: ${toDollars(additionalTip.AMOUNT, true)}`}
                </div>
              );
            })}

            {!hideExtraAdditions && (
              <span
                className="text-indigo-500 font-semibold cursor-pointer"
                onClick={() => {
                  this.additionalTipsModal.openNew(
                    checkPayrollId,
                    row.CHECK_EMPLOYEE_ID,
                    row.LOCATION_ID,
                    row.FULL_NAME
                  );
                }}
              >
                + Additional Tips
              </span>
            )}
          </div>
        );
      },
    };

    let grossPay = {
      width: 1,
      label: grossPayTitle,
      value: "GROSS_PAY",
      format: (value, row) => {
        grossPay = row?.PAY_SUMMARY?.AMOUNT_TOTAL_GROSS ?? 0;

        let extrasGrossPay = extras[row.ID]?.AMOUNT_GROSS_PAY ?? 0;

        return (
          <div>
            {row.ACCOUNT_ID && !hideEarned && (
              <div className="text-gray-800 font-medium">{toDollars(grossPay + extrasGrossPay, true)}</div>
            )}

            {extras[row.ID]?.REIMBURSEMENTS.map((reimbursement) => {
              return (
                <span
                  className="block text-indigo-500 font-semibold cursor-pointer"
                  onClick={() =>
                    this.reimbursementModal.open(
                      checkPayrollId,
                      row.CHECK_EMPLOYEE_ID,
                      row.FULL_NAME,
                      reimbursement
                    )
                  }
                >
                  R: {toDollars(reimbursement.AMOUNT, true)}
                </span>
              );
            })}

            {!hideExtraAdditions && (
              <span
                className="block text-indigo-500 font-semibold cursor-pointer"
                onClick={() =>
                  this.reimbursementModal.openNew(checkPayrollId, row.CHECK_EMPLOYEE_ID, row.FULL_NAME)
                }
              >
                {"+ Reimbursement"}
              </span>
            )}

            <div className="my-1">
              Pay {row.PREF_IS_DIRECT_DEPOSIT && !row.IS_MANUAL ? "by Direct Deposit" : "Manually"}
            </div>
          </div>
        );
      },
    };

    const bonuses = {
      width: 1,
      label: "Other Earnings",
      tooltip:
        "Other earnings. TC stands for tip credit adjustment to minimum wage, and may be automatically applied to your payrun in Payroll Settings.",
      value: "BONUS",
      format: (value, row) => {
        const existingKeys = Object.keys(extras[row.ID] || {}).filter((key) => extras[row.ID][key].length);
        const otherEarnings = Object.keys(SUPPLEMENTAL_TYPES).map((key) => {
          const items = extras[row.ID]?.[key];
          if (!items?.length) return null;
          return (
            <span
              key={key}
              className="text-indigo-500 font-semibold cursor-pointer"
              onClick={() => {
                this.supplementalModal.open(
                  checkPayrollId,
                  row.CHECK_EMPLOYEE_ID,
                  row.FULL_NAME,
                  items[0],
                  key,
                  existingKeys,
                  row.PAY_SUMMARY.AMOUNT_MINUTES
                );
              }}
            >
              {SUPPLEMENTAL_TYPES[key].abbreviation}: {toDollars(items[0].AMOUNT, true)}
            </span>
          );
        });

        return (
          <div className="space-y-1 flex flex-col">
            {otherEarnings}
            {!hideExtraAdditions && (
              <span
                className="text-indigo-500 font-semibold cursor-pointer"
                onClick={() =>
                  this.supplementalModal.openNew(
                    checkPayrollId,
                    row.CHECK_EMPLOYEE_ID,
                    row.FULL_NAME,
                    null,
                    existingKeys,
                    row.PAY_SUMMARY.AMOUNT_MINUTES
                  )
                }
              >
                {"+ Other Earnings"}
              </span>
            )}
          </div>
        );
      },
    };

    if (hideEarned) {
      return [...prefixColumns, ...columns, bonuses, grossPay];
    }

    if (hideExtraAdditions) {
      return [...prefixColumns, ...columns, timeWorked, ...overtimeWorkedArr, payEarned, tips, grossPay];
    }

    return [
      ...prefixColumns,
      ...columns,
      timeWorked,
      ...overtimeWorkedArr,
      payEarned,
      tips,
      bonuses,
      grossPay,
    ];
  }

  renderExtraPay(row, checkPayrollId, type, hours, amount) {
    let {extras} = this.props;

    if (amount > 0) {
      return (
        <div
          className="text-left text-indigo-500 cursor-pointer font-medium"
          onClick={() => {
            let existingEntry = extras[row.ID][type][0];

            this.extraPayModal.open(
              checkPayrollId,
              row.CHECK_EMPLOYEE_ID,
              row.LOCATION_ID,
              type,
              row.FULL_NAME,
              existingEntry
            );
          }}
        >
          <div className="text-medium">{hours} hrs</div>

          <div className="text-indigo-500 cursor-pointer">{toDollars(amount, true)}</div>
        </div>
      );
    }

    return (
      <span
        className="text-indigo-500 font-semibold cursor-pointer"
        onClick={() => {
          this.extraPayModal.openNew(
            checkPayrollId,
            row.CHECK_EMPLOYEE_ID,
            row.LOCATION_ID,
            type,
            row.FULL_NAME
          );
        }}
      >
        + Add
      </span>
    );
  }

  renderTabs() {
    let {dbaLocations, hideHeader, hideEmployeeSelector} = this.props;

    let tabs = fetchPayrollTabs(dbaLocations);

    return (
      <div>
        <div className="mb-4 z-0">
          {!hideHeader && (
            <PayrollRunTableFilter
              dbaLocations={dbaLocations}
              tabs={tabs}
              setSearch={(input) => this.setState({search: input})}
              setSelectedLocations={(input) => this.setState({selectedLocation: input})}
              setSelectedEmployees={(input) => this.setState({selectedEmployees: input})}
              hideEmployeeSelector={hideEmployeeSelector}
            />
          )}
        </div>

        {this.renderTable()}
      </div>
    );
  }

  renderTable() {
    let {search, selectedLocation, selectedEmployees} = this.state;
    let {breakdown, expandSummary, extras, hideEmployeeSelector, breakOptions, updateCard} = this.props;

    let expandable = false;
    if (expandSummary) {
      expandable = (row) => (
        <TimeCardSummaryExpansionComponent
          row={row}
          breakOptions={breakOptions}
          highlightNoPay
          updateCard={updateCard}
        />
      );
    }

    if (search.length > 0) {
      breakdown = new Fuse(breakdown, {
        keys: ["FULL_NAME"],
        useExtendedSearch: true,
        threshold: 0.1,
      })
        .search(search)
        .map(({item}) => item);
    }

    if (selectedLocation !== "all") {
      breakdown = breakdown.filter((breakdownObj) => breakdownObj.LOCATION_ID === selectedLocation);
    }

    if (selectedEmployees !== "all" && !hideEmployeeSelector) {
      breakdown = breakdown.filter(
        (breakdownObj) =>
          breakdownObj?.PAY_SUMMARY?.AMOUNT_TOTAL_GROSS ??
          0 + extras[breakdownObj.ID]?.AMOUNT_GROSS_PAY ??
          0 > 0
      );
    }

    return <Table data={breakdown} columns={this.getBreakdownColumns()} expandable={expandable} />;
  }

  render() {
    let {refreshExtras, dbaLocations, breakdown} = this.props;

    return (
      <div>
        <AdditionalTipsModal
          ref={(e) => (this.additionalTipsModal = e)}
          refresh={refreshExtras}
          dbaLocations={dbaLocations}
        />

        <SupplementalModal
          ref={(e) => (this.supplementalModal = e)}
          refresh={refreshExtras}
          dbaLocations={dbaLocations}
        />

        <ReimbursementModal
          ref={(e) => (this.reimbursementModal = e)}
          refresh={refreshExtras}
          dbaLocations={dbaLocations}
        />

        <ExtraPayModal
          ref={(e) => (this.extraPayModal = e)}
          refresh={refreshExtras}
          dbaLocations={dbaLocations}
        />

        {this.renderTabs()}
      </div>
    );
  }
}

export default setupReduxConnection(["shop", "payroll"])(PayrollRunTable);
